import React, { useState, useEffect } from 'react'
import { ModalBase } from './ModalBase'
import { useAuth } from '@/hooks/useAuth'
import { enrollmentService } from '@/services/enrollmentService'
import { paymentService } from '@/services/paymentService'
import { couponService } from '@/services/couponService'
import { paymentProofService } from '@/services/paymentProofService'
import { getPaymentConfig } from '@/config/payment'
import { PaymentInstructions } from '@/components/payment/PaymentInstructions'
import { PaymentStatusPoller } from '@/components/payment/PaymentStatusPoller'
import type { Enrollment } from '@/types/enrollment'
import type { PaymentTransaction, PaymentChannel } from '@/types/payment'
import { groupPaymentChannelsByMethod, formatPaymentMethodLabel } from '@/types/payment'
import type { Coupon } from '@/types/coupon'
import { getCouponErrorMessage, formatDiscount } from '@/types/coupon'
import { FileUpload } from '@/components/common/FileUpload'
import { useQueryClient } from '@tanstack/react-query'

interface Course {
    id: string
    title: string
    price: number
    image?: string
    certPrice?: number
}

interface CheckoutModalProps {
    isOpen: boolean
    onClose: () => void
    course: Course
    type?: 'course' | 'certificate'
    onEnrollmentCreated?: (enrollment: Enrollment) => void
    customerName?: string
    customerEmail?: string
    customerPhone?: string
}

type CheckoutStep = 'review' | 'select_payment' | 'processing' | 'payment_pending' | 'manual_transfer' | 'success' | 'error'

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
    isOpen,
    onClose,
    course,
    type = 'course',
    onEnrollmentCreated,
    customerName = '',
    customerEmail = '',
    customerPhone = '',
}) => {
    const { user } = useAuth()
    const [step, setStep] = useState<CheckoutStep>('review')
    const [enrollment, setEnrollment] = useState<Enrollment | null>(null)
    const [payment, setPayment] = useState<PaymentTransaction | null>(null)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [selectedChannel, setSelectedChannel] = useState<PaymentChannel | null>(null)
    const [couponCode, setCouponCode] = useState<string>('')
    const [discountAmount, setDiscountAmount] = useState<number>(0)
    const [couponError, setCouponError] = useState<string>('')
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false)
    const [validatedCoupon, setValidatedCoupon] = useState<Coupon | null>(null)
    const [bankConfig, setBankConfig] = useState<{ bankName: string; accountNumber: string; accountHolderName: string } | null>(null)

    // Payment proof upload state
    const [proofBankName, setProofBankName] = useState('')
    const [proofAccountNumber, setProofAccountNumber] = useState('')
    const [proofAccountHolder, setProofAccountHolder] = useState('')
    const [proofFileUrl, setProofFileUrl] = useState('')
    const [proofNotes, setProofNotes] = useState('')
    const [proofError, setProofError] = useState<string | null>(null)
    const [isSubmittingProof, setIsSubmittingProof] = useState(false)
    const [proofSubmitted, setProofSubmitted] = useState(false)
    const queryClient = useQueryClient()

    const price = type === 'certificate' ? (course.certPrice || 0) : course.price
    const finalPrice = Math.max(0, price - discountAmount)
    const isFree = finalPrice === 0

    const paymentMethodGroups = groupPaymentChannelsByMethod()

    useEffect(() => {
        if (isOpen) {
            setStep('review')
            setEnrollment(null)
            setPayment(null)
            setErrorMessage('')
            setSelectedChannel(null)
            setCouponCode('')
            setDiscountAmount(0)
            setCouponError('')
            setValidatedCoupon(null)
            setProofBankName('')
            setProofAccountNumber('')
            setProofAccountHolder('')
            setProofFileUrl('')
            setProofNotes('')
            setProofError(null)
            setIsSubmittingProof(false)
            setProofSubmitted(false)
        }
    }, [isOpen])

    useEffect(() => {
        if (step === 'manual_transfer' && !bankConfig) {
            getPaymentConfig().then(setBankConfig)
        }
    }, [step, bankConfig])

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return

        setIsApplyingCoupon(true)
        setCouponError('')
        setValidatedCoupon(null)

        try {
            const result = await couponService.validateCoupon({
                code: couponCode.trim().toUpperCase(),
                originalPrice: price,
            })

            if (result.valid && result.coupon) {
                setDiscountAmount(result.discountAmount)
                setValidatedCoupon(result.coupon)
                setCouponError('')
            } else {
                const errorMessage = result.errorCode
                    ? getCouponErrorMessage(result.errorCode)
                    : result.errorMessage || 'Kupon tidak valid'
                setCouponError(errorMessage)
                setDiscountAmount(0)
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Gagal memvalidasi kupon'
            setCouponError(message)
            setDiscountAmount(0)
        } finally {
            setIsApplyingCoupon(false)
        }
    }

    const handleRemoveCoupon = () => {
        setCouponCode('')
        setDiscountAmount(0)
        setCouponError('')
        setValidatedCoupon(null)
    }

    const handleProceedToPayment = () => {
        if (isFree) {
            handleFreeEnrollment()
        } else {
            setStep('select_payment')
        }
    }

    const handleFreeEnrollment = async () => {
        if (type === 'certificate') {
            setErrorMessage('Certificate purchase is not available yet')
            setStep('error')
            return
        }

        setStep('processing')
        setErrorMessage('')

        try {
            const enrollmentResponse = await enrollmentService.enrollInCourse(
                course.id,
                undefined,
                validatedCoupon?.id,
                discountAmount > 0 ? discountAmount : undefined
            )

            if (!enrollmentResponse.data) {
                throw new Error('No enrollment data returned')
            }

            if (validatedCoupon && discountAmount > 0) {
                try {
                    await couponService.applyCoupon({
                        couponId: validatedCoupon.id,
                        enrollmentId: enrollmentResponse.data.id,
                        discountAmount: discountAmount,
                    })
                } catch (couponError) {
                    console.error('Failed to apply coupon:', couponError)
                }
            }

            setEnrollment(enrollmentResponse.data)
            setStep('success')
            onEnrollmentCreated?.(enrollmentResponse.data)
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to process enrollment'
            setErrorMessage(message)
            setStep('error')
        }
    }

    const handleCreatePayment = async () => {
        if (!selectedChannel) {
            setErrorMessage('Please select a payment method')
            return
        }

        if (type === 'certificate') {
            setErrorMessage('Certificate purchase is not available yet')
            setStep('error')
            return
        }

        setStep('processing')
        setErrorMessage('')

        try {
            // Step 1: Create enrollment first
            const enrollmentResponse = await enrollmentService.enrollInCourse(
                course.id,
                undefined,
                validatedCoupon?.id,
                discountAmount > 0 ? discountAmount : undefined
            )

            if (!enrollmentResponse.data) {
                throw new Error('No enrollment data returned')
            }

            // Apply coupon if one was used
            if (validatedCoupon && discountAmount > 0) {
                try {
                    await couponService.applyCoupon({
                        couponId: validatedCoupon.id,
                        enrollmentId: enrollmentResponse.data.id,
                        discountAmount: discountAmount,
                    })
                } catch (couponError) {
                    console.error('Failed to apply coupon:', couponError)
                }
            }

            setEnrollment(enrollmentResponse.data)
            onEnrollmentCreated?.(enrollmentResponse.data)

            // Step 2: Create payment for the enrollment
            const paymentResponse = await paymentService.createPayment({
                enrollmentId: enrollmentResponse.data.id,
                paymentChannel: selectedChannel,
                customerName: customerName || 'Customer',
                customerEmail: customerEmail || 'customer@example.com',
                customerPhone: customerPhone,
                couponCode: couponCode || undefined,
                discountAmount: discountAmount,
            })

            setPayment(paymentResponse)
            setStep('payment_pending')
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to process payment'
            setErrorMessage(message)
            setStep('error')
        }
    }

    const handlePaymentSuccess = () => {
        setStep('success')
    }

    const handlePaymentFailed = () => {
        setErrorMessage('Payment failed. Please try again.')
        setStep('error')
    }

    const handlePaymentExpired = () => {
        setErrorMessage('Payment expired. Please try again.')
        setStep('error')
    }

    const handleCopyPaymentCode = () => {
        // Could add toast notification here
    }

    const handleManualTransfer = async () => {
        if (type === 'certificate') {
            setErrorMessage('Certificate purchase is not available yet')
            setStep('error')
            return
        }

        setStep('processing')
        setErrorMessage('')

        try {
            let enrollmentData: Enrollment

            try {
                // Try to create enrollment
                const enrollmentResponse = await enrollmentService.enrollInCourse(
                    course.id,
                    undefined,
                    validatedCoupon?.id,
                    discountAmount > 0 ? discountAmount : undefined
                )

                if (!enrollmentResponse.data) {
                    throw new Error('No enrollment data returned')
                }

                enrollmentData = enrollmentResponse.data

                // Apply coupon if one was used
                if (validatedCoupon && discountAmount > 0) {
                    try {
                        await couponService.applyCoupon({
                            couponId: validatedCoupon.id,
                            enrollmentId: enrollmentData.id,
                            discountAmount: discountAmount,
                        })
                    } catch (couponError) {
                        console.error('Failed to apply coupon:', couponError)
                    }
                }
            } catch (enrollError) {
                // Handle 409 Conflict — enrollment already exists (pending_payment)
                // Retrieve the existing enrollment and proceed to manual_transfer
                if (enrollError instanceof Error && enrollError.message.includes('already enrolled')) {
                    const existingEnrollments = await enrollmentService.getUserEnrollments(user?.id || '', {})
                    const existingEnrollment = existingEnrollments?.data?.find(
                        (e: Enrollment) => e.courseId === course.id && e.paymentStatus === 'pending'
                    )
                    if (existingEnrollment) {
                        enrollmentData = existingEnrollment
                    } else {
                        throw enrollError
                    }
                } else {
                    throw enrollError
                }
            }

            setEnrollment(enrollmentData)
            onEnrollmentCreated?.(enrollmentData)
            setStep('manual_transfer')
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to process enrollment'
            setErrorMessage(message)
            setStep('error')
        }
    }

    const handleSubmitProof = async () => {
        if (!proofBankName.trim() || !proofAccountNumber.trim() || !proofAccountHolder.trim() || !proofFileUrl.trim()) {
            setProofError('Semua field wajib diisi')
            return
        }
        
        setIsSubmittingProof(true)
        setProofError(null)
        
        try {
            await paymentProofService.submitProof(enrollment!.id, {
                amount_paid: finalPrice,
                bank_name: proofBankName.trim(),
                account_number: proofAccountNumber.trim(),
                account_holder_name: proofAccountHolder.trim(),
                proof_file_url: proofFileUrl.trim(),
                notes: proofNotes.trim() || undefined,
            })
            setProofSubmitted(true)
            queryClient.invalidateQueries({ queryKey: ['payment-proofs', 'me'] })
            queryClient.invalidateQueries({ queryKey: ['enrollments'] })
        } catch (err) {
            setProofError(err instanceof Error ? err.message : 'Gagal mengirim bukti pembayaran')
        } finally {
            setIsSubmittingProof(false)
        }
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
        }).format(amount)
    }

    // Processing step - show loading spinner
    if (step === 'processing') {
        return (
            <ModalBase isOpen={isOpen} onClose={() => {}}>
                <div className="flex flex-col items-center text-center py-12">
                    <div className="size-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-6" />
                    <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
                        Processing...
                    </h3>
                    <p className="text-gray-500 dark:text-text-muted dark:text-gray-400">
                        Please wait while we process your request
                    </p>
                </div>
            </ModalBase>
        )
    }

    // Error step - show error message
    if (step === 'error') {
        return (
            <ModalBase isOpen={isOpen} onClose={onClose}>
                <div className="flex flex-col items-center text-center pt-8 pb-4">
                    <div className="size-24 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mb-6 text-red-500">
                        <span className="material-symbols-outlined text-[48px]">error</span>
                    </div>
                    <h3 className="text-2xl font-bold text-text-main dark:text-white mb-2">
                        Something went wrong
                    </h3>
                    <p className="text-gray-500 dark:text-text-muted dark:text-gray-400 mb-8">
                        {errorMessage || 'An unexpected error occurred. Please try again.'}
                    </p>
                    <button
                        onClick={() => setStep('review')}
                        className="w-full py-4 bg-primary text-black font-bold rounded-full mb-3 shadow-lg active:scale-[0.98] transition-transform"
                    >
                        Try Again
                    </button>
                    <button
                        onClick={onClose}
                        className="w-full py-3 text-gray-500 dark:text-text-muted font-medium"
                    >
                        Cancel
                    </button>
                </div>
            </ModalBase>
        )
    }

    // Payment pending step - show payment instructions
    if (step === 'payment_pending' && payment) {
        return (
            <ModalBase isOpen={isOpen} onClose={onClose}>
                <div className="pb-4">
                    <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white mb-6">
                        Complete Your Payment
                    </h3>

                    <PaymentStatusPoller
                        transactionId={payment.id}
                        onPaymentSuccess={handlePaymentSuccess}
                        onPaymentFailed={handlePaymentFailed}
                        onPaymentExpired={handlePaymentExpired}
                    />

                    <div className="mt-6">
                        <PaymentInstructions
                            payment={payment}
                            onCopyCode={handleCopyPaymentCode}
                        />
                    </div>

                    <button
                        onClick={onClose}
                        className="w-full mt-6 py-3 text-gray-500 dark:text-text-muted font-medium"
                    >
                        Close (payment will continue in background)
                    </button>
                </div>
            </ModalBase>
        )
    }

    // Manual transfer step - show bank account info + upload proof form
    if (step === 'manual_transfer' && enrollment) {
        if (!bankConfig) {
            return (
                <ModalBase isOpen={isOpen} onClose={() => {}}>
                    <div className="flex flex-col items-center text-center py-12">
                        <div className="size-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-6" />
                        <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
                            Loading payment info...
                        </h3>
                    </div>
                </ModalBase>
            )
        }

        // Success state after proof submitted
        if (proofSubmitted) {
            return (
                <ModalBase isOpen={isOpen} onClose={onClose}>
                    <div className="pb-4 text-center">
                        <div className="size-20 bg-emerald-100 dark:bg-emerald-500/20 rounded-full flex items-center justify-center mb-6 mx-auto">
                            <span className="material-symbols-outlined text-[40px] text-emerald-600 dark:text-emerald-400 filled">check_circle</span>
                        </div>
                        <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
                            Bukti Pembayaran Terkirim!
                        </h3>
                        <p className="text-gray-500 dark:text-text-muted mb-6 text-sm">
                            Bukti pembayaran Anda sedang diverifikasi oleh admin. 
                            Anda akan mendapat notifikasi setelah verifikasi selesai.
                        </p>
                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-6 text-left">
                            <p className="text-sm text-amber-800 dark:text-amber-300">
                                <strong>Status:</strong> Menunggu Verifikasi
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-full py-4 bg-primary text-black font-bold rounded-full shadow-lg active:scale-[0.98] transition-transform"
                        >
                            Tutup
                        </button>
                    </div>
                </ModalBase>
            )
        }

        // Upload form state
        return (
            <ModalBase isOpen={isOpen} onClose={onClose}>
                <div className="pb-4">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="material-symbols-outlined text-amber-500">account_balance</span>
                        <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white">
                            Transfer Manual
                        </h3>
                    </div>

                    {/* Bank Account Info */}
                    <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-4 mb-4 space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500 dark:text-text-muted">Bank</span>
                            <span className="font-bold dark:text-white">{bankConfig.bankName}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500 dark:text-text-muted">No. Rekening</span>
                            <span className="font-bold dark:text-white font-mono">{bankConfig.accountNumber}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500 dark:text-text-muted">Atas Nama</span>
                            <span className="font-bold dark:text-white">{bankConfig.accountHolderName}</span>
                        </div>
                        <div className="flex justify-between border-t border-gray-200 dark:border-white/10 pt-2 mt-2">
                            <span className="text-gray-500 dark:text-text-muted text-sm">Jumlah Transfer</span>
                            <span className="text-lg font-bold text-primary">{formatCurrency(finalPrice)}</span>
                        </div>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mb-4">
                        <p className="text-xs text-blue-800 dark:text-blue-300">
                            <strong>Catatan:</strong> Transfer harus sesuai nominal di atas. Setelah transfer, isi form di bawah ini.
                        </p>
                    </div>

                    {/* Upload Proof Form */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold text-text-main dark:text-white">Upload Bukti Pembayaran</h4>
                        
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
                                Bank Pengirim *
                            </label>
                            <input
                                type="text"
                                value={proofBankName}
                                onChange={(e) => setProofBankName(e.target.value)}
                                className="w-full px-3 py-2.5 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary"
                                placeholder="Contoh: BCA, Mandiri"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
                                No. Rekening Pengirim *
                            </label>
                            <input
                                type="text"
                                value={proofAccountNumber}
                                onChange={(e) => setProofAccountNumber(e.target.value)}
                                className="w-full px-3 py-2.5 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary"
                                placeholder="Nomor rekening Anda"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
                                Atas Nama Pengirim *
                            </label>
                            <input
                                type="text"
                                value={proofAccountHolder}
                                onChange={(e) => setProofAccountHolder(e.target.value)}
                                className="w-full px-3 py-2.5 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary"
                                placeholder="Nama pemilik rekening"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
                                Bukti Transfer *
                            </label>
                            <FileUpload
                                type="image"
                                accept="image/*,.pdf"
                                onUpload={(url: string) => setProofFileUrl(url)}
                                label="Upload bukti transfer (image/PDF)"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
                                Catatan (Opsional)
                            </label>
                            <textarea
                                value={proofNotes}
                                onChange={(e) => setProofNotes(e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2.5 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary resize-none"
                                placeholder="Catatan tambahan"
                            />
                        </div>

                        {proofError && (
                            <div className="bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-xs p-3 rounded-lg border border-red-200 dark:border-red-500/20">
                                {proofError}
                            </div>
                        )}

                        <button
                            onClick={handleSubmitProof}
                            disabled={isSubmittingProof}
                            className={`w-full py-3.5 font-bold rounded-full shadow-lg transition-all flex items-center justify-center gap-2 mt-2 ${
                                isSubmittingProof
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-primary text-black hover:shadow-primary/30 active:scale-[0.98]'
                            }`}
                        >
                            {isSubmittingProof ? (
                                <>
                                    <div className="size-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                    Mengirim...
                                </>
                            ) : (
                                <>
                                    Kirim Bukti
                                    <span className="material-symbols-outlined text-lg">send</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </ModalBase>
        )
    }

    // Success step
    if (step === 'success') {
        return (
            <ModalBase isOpen={isOpen} onClose={onClose}>
                <div className="flex flex-col items-center text-center pt-8 pb-4">
                    <div className="size-24 bg-primary/20 rounded-full flex items-center justify-center mb-6 text-primary">
                        <span className="material-symbols-outlined text-[48px] filled">check_circle</span>
                    </div>
                    <h3 className="text-2xl font-bold text-text-main dark:text-white mb-2">
                        {isFree ? 'Enrollment Successful!' : 'Payment Successful!'}
                    </h3>
                    <p className="text-gray-500 dark:text-text-muted dark:text-gray-400 mb-8">
                        {type === 'certificate'
                            ? 'You have unlocked your certificate.'
                            : isFree
                                ? `You have successfully enrolled in ${course.title}`
                                : `You have successfully purchased ${course.title}`}
                    </p>

                    {enrollment && (
                        <div className="w-full bg-gray-50 dark:bg-white/5 rounded-lg p-4 mb-8">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500 dark:text-text-muted">Enrollment ID</span>
                                <span className="font-mono font-medium dark:text-white">#{enrollment.id.slice(0, 8)}</span>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={onClose}
                        className="w-full py-4 bg-primary text-black font-bold rounded-full mb-3 shadow-lg active:scale-[0.98] transition-transform"
                    >
                        {type === 'certificate' ? 'Download Certificate' : 'Start Learning'}
                    </button>
                </div>
            </ModalBase>
        )
    }

    // Select payment method step
    if (step === 'select_payment') {
        return (
            <ModalBase isOpen={isOpen} onClose={onClose}>
                <div className="pb-4">
                    <button
                        onClick={() => setStep('review')}
                        className="flex items-center gap-1 text-gray-500 dark:text-text-muted mb-4 hover:text-text-main transition-colors"
                    >
                        <span className="material-symbols-outlined text-lg">arrow_back</span>
                        <span className="text-sm">Back</span>
                    </button>

                    <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white mb-6">
                        Select Payment Method
                    </h3>

                    <div className="space-y-6 mb-8 max-h-96 overflow-y-auto">
                        {/* Manual Transfer Option */}
                        <div>
                            <h4 className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-3">
                                Transfer Manual
                            </h4>
                            <div
                                onClick={handleManualTransfer}
                                className="flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">🏦</span>
                                    <div>
                                        <p className="font-bold text-sm dark:text-white">Transfer Bank</p>
                                        <p className="text-xs text-gray-500 dark:text-text-muted">Transfer langsung ke rekening kami, lalu upload bukti</p>
                                    </div>
                                </div>
                                <span className="material-symbols-outlined text-gray-400">arrow_forward</span>
                            </div>
                        </div>

                        {/* Online Payment Methods */}
                        {Object.entries(paymentMethodGroups).map(([method, channels]) => (
                            <div key={method}>
                                <h4 className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-3">
                                    {formatPaymentMethodLabel(method as keyof typeof paymentMethodGroups)}
                                </h4>
                                <div className="space-y-2">
                                    {channels.map((channelInfo) => (
                                        <div
                                            key={channelInfo.channel}
                                            onClick={() => setSelectedChannel(channelInfo.channel)}
                                            className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                                                selectedChannel === channelInfo.channel
                                                    ? 'border-2 border-primary bg-primary/5'
                                                    : 'border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="text-2xl">{channelInfo.icon}</span>
                                                <div>
                                                    <p className="font-bold text-sm dark:text-white">{channelInfo.name}</p>
                                                    <p className="text-xs text-gray-500 dark:text-text-muted">{channelInfo.description}</p>
                                                </div>
                                            </div>
                                            <div className={`w-4 h-4 rounded-full ${
                                                selectedChannel === channelInfo.channel
                                                    ? 'border-4 border-primary bg-white'
                                                    : 'border border-gray-300'
                                            }`} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Order Summary */}
                    <div className="border-t border-gray-100 dark:border-white/10 pt-4 mb-6 space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500 dark:text-text-muted">Subtotal</span>
                            <span className="dark:text-white">{formatCurrency(price)}</span>
                        </div>
                        {discountAmount > 0 && (
                            <div className="flex justify-between text-sm text-green-600">
                                <span>Discount</span>
                                <span>-{formatCurrency(discountAmount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-lg font-bold mt-2">
                            <span className="dark:text-white">Total</span>
                            <span className="text-primary">{formatCurrency(finalPrice)}</span>
                        </div>
                    </div>

                    <button
                        onClick={handleCreatePayment}
                        disabled={!selectedChannel}
                        className={`w-full py-4 font-bold rounded-full shadow-lg transition-all flex items-center justify-center gap-2 ${
                            selectedChannel
                                ? 'bg-primary text-black hover:shadow-primary/30 hover:scale-[1.01] active:scale-[0.98]'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        <span>Pay {formatCurrency(finalPrice)}</span>
                        <span className="material-symbols-outlined text-lg">arrow_forward</span>
                    </button>
                </div>
            </ModalBase>
        )
    }

    // Review step - default checkout view
    return (
        <ModalBase isOpen={isOpen} onClose={onClose}>
            <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white mb-6">
                {type === 'certificate' ? 'Purchase Certificate' : isFree ? 'Free Enrollment' : 'Checkout'}
            </h3>

            <div className="flex gap-4 p-3 bg-white dark:bg-black/20 rounded-lg border border-gray-100 dark:border-white/5 mb-6">
                <div
                    className="w-20 h-20 rounded-lg bg-gray-200 bg-cover bg-center flex-shrink-0"
                    style={{ backgroundImage: course.image ? `url(${course.image})` : undefined }}
                />
                <div className="flex flex-col justify-center min-w-0">
                    <p className="text-xs font-bold text-primary uppercase mb-1">
                        {type === 'certificate' ? 'Certificate Fee' : 'Course'}
                    </p>
                    <h4 className="font-bold leading-tight dark:text-white line-clamp-2">{course.title}</h4>
                    <p className="text-sm text-gray-500 dark:text-text-muted mt-1">
                        {isFree ? (
                            <span className="text-green-600 font-semibold">FREE</span>
                        ) : (
                            formatCurrency(price)
                        )}
                    </p>
                </div>
            </div>

            {/* Coupon Code Input */}
            {!isFree && (
                <div className="mb-6">
                    <label className="text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider block mb-2">
                        Have a coupon?
                    </label>
                    {validatedCoupon ? (
                        // Show applied coupon
                        <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-green-600">local_offer</span>
                                <div>
                                    <p className="font-bold text-green-800 dark:text-green-400">{validatedCoupon.code}</p>
                                    <p className="text-sm text-green-600 dark:text-green-500">
                                        {formatDiscount(validatedCoupon)} off
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleRemoveCoupon}
                                className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-800/30 rounded-lg transition-colors"
                            >
                                <span className="material-symbols-outlined text-lg">close</span>
                            </button>
                        </div>
                    ) : (
                        // Show input field
                        <>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    placeholder="Enter coupon code"
                                    className="flex-1 px-4 py-3 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-text-main dark:text-white focus:outline-none focus:border-primary"
                                />
                                <button
                                    onClick={handleApplyCoupon}
                                    disabled={isApplyingCoupon || !couponCode.trim()}
                                    className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                                        isApplyingCoupon || !couponCode.trim()
                                            ? 'bg-gray-100 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                            : 'bg-primary/10 text-primary hover:bg-primary/20'
                                    }`}
                                >
                                    {isApplyingCoupon ? '...' : 'Apply'}
                                </button>
                            </div>
                            {couponError && (
                                <p className="text-red-500 text-sm mt-1">{couponError}</p>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Order Summary */}
            <div className="border-t border-gray-100 dark:border-white/10 pt-4 mb-8 space-y-2">
                <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-text-muted">Subtotal</span>
                    <span className="font-bold dark:text-white">
                        {isFree ? 'FREE' : formatCurrency(price)}
                    </span>
                </div>
                {discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                        <span>Discount ({couponCode})</span>
                        <span>-{formatCurrency(discountAmount)}</span>
                    </div>
                )}
                <div className="flex justify-between text-lg font-bold mt-4">
                    <span className="dark:text-white">Total</span>
                    <span className="dark:text-white">
                        {isFree ? (
                            <span className="text-green-600">FREE</span>
                        ) : (
                            formatCurrency(finalPrice)
                        )}
                    </span>
                </div>
            </div>

            <button
                onClick={handleProceedToPayment}
                className="w-full py-4 bg-primary text-black font-bold rounded-full shadow-lg hover:shadow-primary/30 hover:scale-[1.01] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
                <span>{isFree ? 'Enroll Now' : 'Select Payment Method'}</span>
                <span className="material-symbols-outlined text-lg">arrow_forward</span>
            </button>
        </ModalBase>
    )
}

export default CheckoutModal
